<script setup lang="ts">
    import {computed} from 'vue';

    const props = withDefaults(defineProps<{part: 'greeting' | 'topics'; disabled: boolean; carLabel?: string; carPlate?: string}>(), {
        carLabel: '',
        carPlate: '',
    });

    /**
     * The shop already had a car selected, so the conversation was seeded with it
     * (ConversationStore::seedCarOnce) and asking for a kenteken here would be asking for something
     * we have. The greeting names the car instead.
     */
    const knownCar = computed(() => props.carLabel.trim());

    /** Built here rather than in the template: interpolating the plate inline left the sentence at
     *  the mercy of how the template's whitespace collapses ("Golf VII(12-ABC-3) ,"). */
    const carDescription = computed(() => (props.carPlate.trim() ? `${knownCar.value} (${props.carPlate.trim()})` : knownCar.value));
    const topicTitle = computed(() => (knownCar.value ? 'Kies een onderwerp' : 'Of kies een onderwerp'));

    const emit = defineEmits<{(event: 'ask', message: string): void}>();

    /**
     * Starter topics, each one a message the customer would otherwise have typed - clicking sends
     * it verbatim, so the turn reaches Claude exactly as free text does and nothing here has to
     * decide what the customer meant.
     *
     * The design's fourth topic, "Ik wil een medewerker spreken", is left out: there is no
     * hand-off to a human agent in this chat (see the note in ChatTranscript.vue), so the button
     * would open a route that dead-ends.
     */
    const topics = ['Ik zoek een auto onderdeel', 'Waar is mijn bestelling?', 'Ik wil iets retourneren'];
</script>

<template>
    <div v-if="part === 'greeting'">
        <div class="chat-bubble chat-bubble--ai">
            <div class="mb-5">Hoi! Ik ben de Onderdelenhulp.</div>
            <!-- No punctuation straight after </strong>: prettier may wrap there, and Vue
                         renders that newline as a space (" ,"). A following word is always fine. -->
            <div v-if="knownCar">
                Ik zoek onderdelen voor je
                <strong>{{ carDescription }}</strong>
                en kijk ook je bestelling na of help je met een retour.
            </div>
            <div v-else>
                Ik zoek het juiste onderdeel voor jouw auto, kijk je bestelling na en help je met een retour. Stuur me je kenteken of
                VIN-nummer - dan toon ik alleen onderdelen die passen.
            </div>
        </div>
    </div>
    <div v-else>
        <div class="text-muted mb-10">{{ topicTitle }}</div>
        <div class="chat-pills">
            <button
                v-for="topic in topics"
                :key="topic"
                type="button"
                class="btn btn-default"
                :disabled="disabled"
                @click="emit('ask', topic)">
                {{ topic }}
            </button>
        </div>
    </div>
</template>
